import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../css/ProductAddPage.css";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/header/AdminHeader";
import { toast } from "react-hot-toast";

function ProductAddPage() {
    const navigate = useNavigate();
    const apiUrl = `${process.env.REACT_APP_API}`;

    // Common product fields (shared across variants)
    const [productName, setProductName] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [productCategory, setProductCategory] = useState([]);
    const [subCategory, setSubCategory] = useState([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [featured, setFeatured] = useState("no");

    // Variants array
    const [variants, setVariants] = useState([
        {
            color: "",
            price: "",
            sizes: [{ label: "", quantity: "" }],
            images: [],
            imagePreviews: [],
            aiGeneratedImage: null, // NEW: Store AI-generated image
            aiGeneratedPreview: null, // NEW: Preview URL
            isGeneratingAI: false, // NEW: Loading state
            useAIImage: false, // NEW: Flag to use AI image instead of uploaded
            // Video fields
            aiGeneratedVideo: null,
            isGeneratingVideo: false,
        },
    ]);

    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    // Fetch categories
    useEffect(() => {
        axios
            .get(`${apiUrl}/categories`)
            .then((response) => {
                setProductCategory(response.data);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    // Fetch subcategories when category changes
    useEffect(() => {
        if (
            selectedCategory.toString() === "6413790302e7ee19ff5c8326" ||
            selectedCategory.toString() === "641378fd02e7ee19ff5c8323" ||
            selectedCategory.toString() === "6413790602e7ee19ff5c8329" ||
            selectedCategory.toString() === "6413790e02e7ee19ff5c832c"
        ) {
            axios
                .get(`${apiUrl}/subcategories/${selectedCategory}`)
                .then((response) => {
                    setSubCategory(response.data.subCategories);
                });
        } else {
            setSubCategory([]);
        }
        setSelectedSubCategory("");
    }, [selectedCategory]);

    // Handle variant-specific images (max 4)
    const handleVariantImagesChange = (e, variantIndex) => {
        const files = Array.from(e.target.files);

        // Limit to 4 images
        if (files.length > 4) {
            toast.error("Maximum 4 images allowed per variant");
            return;
        }

        const newVariants = [...variants];
        newVariants[variantIndex].images = files;

        // Generate previews
        const previews = files.map((file) => URL.createObjectURL(file));
        newVariants[variantIndex].imagePreviews = previews;

        // Reset AI image if user uploads new images
        newVariants[variantIndex].aiGeneratedImage = null;
        newVariants[variantIndex].aiGeneratedPreview = null;
        newVariants[variantIndex].useAIImage = false;

        setVariants(newVariants);
    };

    // Remove variant-specific image
    const removeVariantImage = (variantIndex, imageIndex) => {
        const newVariants = [...variants];
        newVariants[variantIndex].images = newVariants[variantIndex].images.filter(
            (_, i) => i !== imageIndex
        );
        newVariants[variantIndex].imagePreviews = newVariants[
            variantIndex
            ].imagePreviews.filter((_, i) => i !== imageIndex);
        setVariants(newVariants);
    };

    // NEW: Generate AI Model Image
    // Update this function in ProductAddPage.js
    // NEW: Approve AI Image (use it instead of flat-lay)

    const handleGenerateAIImage = async (variantIndex) => {
        const variant = variants[variantIndex];

        // Validation: Need at least one image to generate from
        if (!variant.images || variant.images.length === 0) {
            toast.error("Please upload at least one flat-lay image first");
            return;
        }

        // Validation: Need category selected
        if (!selectedCategory) {
            toast.error("Please select a category first");
            return;
        }

        // Set loading state FIRST with immutable update
        setVariants(prevVariants =>
            prevVariants.map((v, idx) =>
                idx === variantIndex
                    ? { ...v, isGeneratingAI: true }
                    : v
            )
        );

        try {
            console.log("🚀 Starting AI image generation...");
            console.log("Variant index:", variantIndex);
            console.log("Product name:", productName);
            console.log("Category:", selectedCategory);
            console.log("Image file:", variant.images[0]);

            // Create form data with the first image (flat-lay)
            const formData = new FormData();
            formData.append("flatLayImage", variant.images[0]);
            formData.append("productName", productName);
            formData.append("productCategory", selectedCategory);

            console.log("📤 Sending request to:", `${apiUrl}/generate-model-preview`);

            // Call API to generate AI model image
            const response = await axios.post(
                `${apiUrl}/generate-model-preview`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    },
                    timeout: 120000, // 2 minutes timeout for AI generation
                }
            );

            console.log("✅ Response received:", response.data);

            // Check if response is successful
            if (response.data.success && response.data.previewUrl) {
                console.log("🎉 AI image URL:", response.data.previewUrl);

                // Update with AI-generated image using immutable pattern
                setVariants(prevVariants =>
                    prevVariants.map((v, idx) =>
                        idx === variantIndex
                            ? {
                                ...v,
                                aiGeneratedPreview: response.data.previewUrl,
                                aiGeneratedImage: response.data.previewUrl,
                                isGeneratingAI: false
                            }
                            : v
                    )
                );

                toast.success("AI model image generated successfully!");
            } else {
                console.error("❌ Unexpected response format:", response.data);

                // Reset loading state
                setVariants(prevVariants =>
                    prevVariants.map((v, idx) =>
                        idx === variantIndex
                            ? { ...v, isGeneratingAI: false }
                            : v
                    )
                );

                toast.error("Unexpected response from server");
            }
        } catch (error) {
            console.error("❌ ERROR generating AI image:");
            console.error("Error message:", error.message);
            console.error("Error response status:", error.response && error.response.status);
            console.error("Error response data:", error.response && error.response.data);
            console.error("Full error:", error);

            // Reset loading state on error
            setVariants(prevVariants =>
                prevVariants.map((v, idx) =>
                    idx === variantIndex
                        ? { ...v, isGeneratingAI: false }
                        : v
                )
            );

            const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Failed to generate AI image";
            toast.error(errorMessage);
        }
    };
    const handleApproveAIImage = (variantIndex) => {
        const newVariants = [...variants];
        newVariants[variantIndex].useAIImage = true;
        setVariants(newVariants);
        toast.success("AI image approved! It will be used as the product image.");
    };

    // NEW: Reject AI Image (go back to flat-lay)
    const handleRejectAIImage = (variantIndex) => {
        const newVariants = [...variants];
        newVariants[variantIndex].aiGeneratedImage = null;
        newVariants[variantIndex].aiGeneratedPreview = null;
        newVariants[variantIndex].useAIImage = false;
        setVariants(newVariants);
        toast("AI image removed. Using original images.");
    };

    // Generate AI Video
    const handleGenerateVideo = async (variantIndex) => {
        const variant = variants[variantIndex];

        if (!variant.aiGeneratedImage) {
            toast.error("Please generate and approve an AI image first");
            return;
        }

        setVariants(prevVariants =>
            prevVariants.map((v, idx) =>
                idx === variantIndex ? { ...v, isGeneratingVideo: true } : v
            )
        );

        try {
            toast("🎬 Video generation started, we'll notify you when done...", {
                duration: 5000,
            });

            // Step 1: Start the job — returns immediately
            const response = await axios.post(
                `${apiUrl}/generate-video-preview`,
                { imageUrl: variant.aiGeneratedImage },
                { timeout: 15000 } // short timeout — just for job creation
            );

            const { jobId } = response.data;
            console.log("🎬 Job started:", jobId);

            // Step 2: Poll for completion
            let finished = false;
            const pollInterval = setInterval(async () => {
                try {
                    const statusRes = await axios.get(`${apiUrl}/video-job-status/${jobId}`);
                    const { status, videoUrl, error } = statusRes.data;

                    console.log("⏳ Job status:", status);

                    if (status === "completed" && videoUrl) {
                        finished = true;
                        clearInterval(pollInterval);
                        setVariants(prevVariants =>
                            prevVariants.map((v, idx) =>
                                idx === variantIndex
                                    ? { ...v, aiGeneratedVideo: videoUrl, isGeneratingVideo: false }
                                    : v
                            )
                        );
                        toast.success("🎬 Video generated successfully!");
                    } else if (status === "failed") {
                        finished = true;
                        clearInterval(pollInterval);
                        setVariants(prevVariants =>
                            prevVariants.map((v, idx) =>
                                idx === variantIndex ? { ...v, isGeneratingVideo: false } : v
                            )
                        );
                        toast.error(error || "Video generation failed");
                    }
                } catch (pollError) {
                    console.error("Poll error:", pollError);
                }
            }, 5000); // poll every 5 seconds

            // Renders take 5-8 minutes; the server gives up at 15, so wait a touch longer.
            setTimeout(() => {
                if (finished) return;
                clearInterval(pollInterval);
                setVariants(prevVariants =>
                    prevVariants.map((v, idx) =>
                        idx === variantIndex ? { ...v, isGeneratingVideo: false } : v
                    )
                );
                toast.error("The video is taking longer than expected. Please try again in a few minutes.");
            }, 16 * 60 * 1000);

        } catch (error) {
            setVariants(prevVariants =>
                prevVariants.map((v, idx) =>
                    idx === variantIndex ? { ...v, isGeneratingVideo: false } : v
                )
            );
            toast.error(error.message || "Failed to start video generation");
        }
    };

    // Add new variant
    const handleAddVariant = () => {
        setVariants([
            ...variants,
            {
                color: "",
                price: "",
                sizes: [{ label: "", quantity: "" }],
                images: [],
                imagePreviews: [],
                aiGeneratedImage: null,
                aiGeneratedPreview: null,
                isGeneratingAI: false,
                useAIImage: false,
                // Video fields
                aiGeneratedVideo: null,
                isGeneratingVideo: false,
            },
        ]);
    };

    // Remove variant
    const handleRemoveVariant = (index) => {
        if (variants.length > 1) {
            const newVariants = variants.filter((_, i) => i !== index);
            setVariants(newVariants);
        }
    };

    // Handle variant field changes
    const handleVariantChange = (index, field, value) => {
        const newVariants = [...variants];
        newVariants[index][field] = value;
        setVariants(newVariants);
    };

    // Handle size changes within a variant
    const handleSizeChange = (variantIndex, sizeIndex, field, value) => {
        const newVariants = [...variants];
        newVariants[variantIndex].sizes[sizeIndex][field] = value;
        setVariants(newVariants);
    };

    // Add size to variant
    const handleAddSize = (variantIndex) => {
        const newVariants = [...variants];
        newVariants[variantIndex].sizes.push({ label: "", quantity: "" });
        setVariants(newVariants);
    };

    // Remove size from variant
    const handleRemoveSize = (variantIndex, sizeIndex) => {
        const newVariants = [...variants];
        if (newVariants[variantIndex].sizes.length > 1) {
            newVariants[variantIndex].sizes = newVariants[variantIndex].sizes.filter(
                (_, i) => i !== sizeIndex
            );
            setVariants(newVariants);
        }
    };

    // Improve description using AI
    const improveDescription = () => {
        axios
            .post(`${apiUrl}/improve-description`, {
                productDescription: productDescription,
            })
            .then((response) => {
                setProductDescription(response.data.improvedDescription);
            })
            .catch((error) => {
                console.error("Error improving description:", error);
            });
    };

    // Validation
    const validateForm = () => {
        let errors = {};

        if (!productName.trim()) {
            errors.productName = "Product name is required";
        } else if (productName.length < 3 || productName.length > 50) {
            errors.productName = "Product name must be between 3 and 50 characters";
        }

        if (!selectedCategory) {
            errors.productCategory = "Category is required";
        }

        if (
            (selectedCategory === "6413790302e7ee19ff5c8326" ||
                selectedCategory === "641378fd02e7ee19ff5c8323" ||
                selectedCategory === "6413790602e7ee19ff5c8329" ||
                selectedCategory === "6413790e02e7ee19ff5c832c") &&
            !selectedSubCategory
        ) {
            errors.subCategory = "Subcategory is required";
        }

        // Validate each variant
        variants.forEach((variant, index) => {
            if (!variant.color.trim()) {
                errors[`variant_${index}_color`] = `Variant ${index + 1}: Color is required`;
            }

            if (!variant.price || isNaN(variant.price) || variant.price <= 0) {
                errors[`variant_${index}_price`] = `Variant ${index + 1}: Valid price is required`;
            }

            // Check if at least one size has values
            const hasValidSize = variant.sizes.some(
                (size) => size.label.trim() && size.quantity > 0
            );
            if (!hasValidSize) {
                errors[`variant_${index}_sizes`] = `Variant ${index + 1}: At least one size with quantity is required`;
            }

            // MODIFIED: Check images or AI image
            if (!variant.useAIImage && (!variant.images || variant.images.length === 0)) {
                errors[`variant_${index}_images`] = `Variant ${index + 1}: At least 1 image is required (or generate AI image)`;
            }
        });

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Submit form
    const handleProductSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            toast.error("Please fix the errors in the form");
            return;
        }

        const formData = new FormData();

        // Add common fields
        formData.append("productName", productName);
        formData.append("productDescription", productDescription);
        formData.append("productCategory", selectedCategory);
        formData.append("subCategory", selectedSubCategory);
        formData.append("isFeatured", featured === "yes");

        // Add variants data
        formData.append(
            "variants",
            JSON.stringify(
                variants.map((v) => ({
                    color: v.color,
                    price: v.price,
                    sizes: v.sizes.filter((s) => s.label.trim() && s.quantity > 0),
                    useAIImage: v.useAIImage, // NEW: Flag to indicate AI image usage
                    aiImageUrl: v.aiGeneratedImage, // NEW: AI image URL if approved
                    videoUrl: v.aiGeneratedVideo || null,  // ADD THIS
                }))
            )
        );

        // Add variant-specific images
        variants.forEach((variant, variantIndex) => {
            if (variant.useAIImage) {
                // If using AI image, send the AI image URL
                formData.append(`variant${variantIndex}_aiImageUrl`, variant.aiGeneratedImage);
                formData.append(`variant${variantIndex}_imageCount`, 1);
            } else {
                // Otherwise, use uploaded images
                variant.images.forEach((image, imageIndex) => {
                    formData.append(`variant${variantIndex}_image${imageIndex}`, image);
                });
                formData.append(`variant${variantIndex}_imageCount`, variant.images.length);
            }
        });
        // Add video URL if generated
        variants.forEach((variant, variantIndex) => {
            if (variant.aiGeneratedVideo) {
                formData.append(`variant${variantIndex}_videoUrl`, variant.aiGeneratedVideo);
            }
        });

// Also append as top level for single product
        if (variants[0] && variants[0].aiGeneratedVideo) {
            formData.append("videoUrl", variants[0].aiGeneratedVideo);
        }

        toast
            .promise(axios.post(`${apiUrl}/product/bulk`, formData), {
                loading: "Adding Products...",
                success: "Products Added Successfully!",
                error: "Products could not be added.",
            })
            .then((response) => {
                console.log("Success:", response);
                navigate("/admin/product/view");
            })
            .catch((error) => {
                console.error("Error:", error);
            });
    };

    return (
        <>
            <AdminHeader />
            <div className="container pt-5">
                <div className="mt-5 pt-3">
                    <div className="card shadow-lg p-4">
                        <h1 className="text-center mb-4">Add New Product</h1>

                        <form onSubmit={handleProductSubmit} encType="multipart/form-data">
                            {/* Product Name */}
                            <div className="row mb-3">
                                <label htmlFor="productName" className="col-sm-4 col-form-label">
                                    Product Name <span className="text-danger">*</span>
                                </label>
                                <div className="col-sm-8">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="productName"
                                        value={productName}
                                        onChange={(e) => setProductName(e.target.value)}
                                    />
                                    {formErrors.productName && (
                                        <span className="text-danger">{formErrors.productName}</span>
                                    )}
                                </div>
                            </div>

                            {/* Category */}
                            <div className="row mb-3">
                                <label htmlFor="categoryGender" className="col-sm-4 col-form-label">
                                    Category <span className="text-danger">*</span>
                                </label>
                                <div className="col-sm-8">
                                    <select
                                        className="form-select"
                                        id="categoryGender"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="">Select a category</option>
                                        {productCategory.map((data) => (
                                            <option key={data._id} value={data._id}>
                                                {data.name}
                                            </option>
                                        ))}
                                    </select>
                                    {formErrors.productCategory && (
                                        <span className="text-danger">{formErrors.productCategory}</span>
                                    )}
                                </div>
                            </div>

                            {/* Subcategory (conditional) */}
                            {(selectedCategory.toString() === "6413790302e7ee19ff5c8326" ||
                                selectedCategory.toString() === "641378fd02e7ee19ff5c8323" ||
                                selectedCategory.toString() === "6413790602e7ee19ff5c8329" ||
                                selectedCategory.toString() === "6413790e02e7ee19ff5c832c") && (
                                <div className="row mb-3">
                                    <label htmlFor="productType" className="col-sm-4 col-form-label">
                                        Subcategory <span className="text-danger">*</span>
                                    </label>
                                    <div className="col-sm-8">
                                        <select
                                            className="form-select"
                                            id="productType"
                                            value={selectedSubCategory}
                                            onChange={(e) => setSelectedSubCategory(e.target.value)}
                                        >
                                            <option value="">Select a subcategory</option>
                                            {subCategory.map((data) => (
                                                <option key={data._id} value={data._id}>
                                                    {data.name}
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.subCategory && (
                                            <span className="text-danger">{formErrors.subCategory}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <div className="row mb-3">
                                <label htmlFor="productDescription" className="col-sm-4 col-form-label">
                                    Description
                                </label>
                                <div className="col-sm-8">
                  <textarea
                      className="form-control"
                      id="productDescription"
                      rows="5"
                      value={productDescription}
                      onChange={(e) => setProductDescription(e.target.value)}
                      placeholder="Product description..."
                  ></textarea>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary mt-2"
                                        onClick={improveDescription}
                                    >
                                        ✨ Improve with AI
                                    </button>
                                </div>
                            </div>

                            {/* Featured */}
                            <div className="row mb-3">
                                <label htmlFor="featured" className="col-sm-4 col-form-label">
                                    Featured
                                </label>
                                <div className="col-sm-8">
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="featured"
                                            id="featuredYes"
                                            value="yes"
                                            checked={featured === "yes"}
                                            onChange={(e) => setFeatured(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="featuredYes">
                                            Yes
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="featured"
                                            id="featuredNo"
                                            value="no"
                                            checked={featured === "no"}
                                            onChange={(e) => setFeatured(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="featuredNo">
                                            No
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Variants Section */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <h4 className="mb-3">🎨 Product Variants (Colors)</h4>

                                    {variants.map((variant, variantIndex) => (
                                        <div key={variantIndex} className="card p-3 mb-3 border-primary">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h5 className="mb-0">Variant {variantIndex + 1}</h5>
                                                {variants.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleRemoveVariant(variantIndex)}
                                                    >
                                                        Remove Variant
                                                    </button>
                                                )}
                                            </div>

                                            {/* Color */}
                                            <div className="row mb-3">
                                                <label className="col-sm-3 col-form-label">
                                                    Color <span className="text-danger">*</span>
                                                </label>
                                                <div className="col-sm-9">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="e.g., Red, Blue, Green"
                                                        value={variant.color}
                                                        onChange={(e) =>
                                                            handleVariantChange(variantIndex, "color", e.target.value)
                                                        }
                                                    />
                                                    {formErrors[`variant_${variantIndex}_color`] && (
                                                        <span className="text-danger">
                              {formErrors[`variant_${variantIndex}_color`]}
                            </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Price */}
                                            <div className="row mb-3">
                                                <label className="col-sm-3 col-form-label">
                                                    Price <span className="text-danger">*</span>
                                                </label>
                                                <div className="col-sm-9">
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        value={variant.price}
                                                        onChange={(e) =>
                                                            handleVariantChange(variantIndex, "price", e.target.value)
                                                        }
                                                    />
                                                    {formErrors[`variant_${variantIndex}_price`] && (
                                                        <span className="text-danger">
                              {formErrors[`variant_${variantIndex}_price`]}
                            </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Sizes */}
                                            <div className="row mb-3">
                                                <label className="col-sm-3 col-form-label">
                                                    Sizes & Quantities <span className="text-danger">*</span>
                                                </label>
                                                <div className="col-sm-9">
                                                    {variant.sizes.map((size, sizeIndex) => (
                                                        <div key={sizeIndex} className="row mb-2">
                                                            <div className="col-5">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Size (e.g., S, M, L)"
                                                                    value={size.label}
                                                                    onChange={(e) =>
                                                                        handleSizeChange(
                                                                            variantIndex,
                                                                            sizeIndex,
                                                                            "label",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-5">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    placeholder="Quantity"
                                                                    value={size.quantity}
                                                                    onChange={(e) =>
                                                                        handleSizeChange(
                                                                            variantIndex,
                                                                            sizeIndex,
                                                                            "quantity",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-2">
                                                                {variant.sizes.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm w-100"
                                                                        onClick={() =>
                                                                            handleRemoveSize(variantIndex, sizeIndex)
                                                                        }
                                                                    >
                                                                        ×
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm mt-2"
                                                        onClick={() => handleAddSize(variantIndex)}
                                                    >
                                                        + Add Size
                                                    </button>
                                                    {formErrors[`variant_${variantIndex}_sizes`] && (
                                                        <div className="text-danger mt-2">
                                                            {formErrors[`variant_${variantIndex}_sizes`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Variant-specific images */}
                                            <div className="row mb-3">
                                                <label className="col-sm-3 col-form-label">
                                                    Product Images <span className="text-danger">*</span>
                                                </label>
                                                <div className="col-sm-9">
                                                    <input
                                                        type="file"
                                                        className="form-control mb-2"
                                                        accept="image/*"
                                                        multiple
                                                        onChange={(e) => handleVariantImagesChange(e, variantIndex)}
                                                    />
                                                    <small className="text-muted">
                                                        Upload 1-4 flat-lay images for this variant
                                                    </small>

                                                    {variant.imagePreviews.length > 0 && (
                                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                                            {variant.imagePreviews.map((preview, imageIndex) => (
                                                                <div key={imageIndex} className="position-relative">
                                                                    <img
                                                                        src={preview}
                                                                        alt={`Variant ${imageIndex + 1}`}
                                                                        style={{
                                                                            width: "80px",
                                                                            height: "80px",
                                                                            objectFit: "cover",
                                                                            borderRadius: "8px",
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                                        style={{ padding: "2px 6px", fontSize: "12px" }}
                                                                        onClick={() =>
                                                                            removeVariantImage(variantIndex, imageIndex)
                                                                        }
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* NEW: AI Image Generation Section */}
                                                    {variant.images.length > 0 && !variant.useAIImage && (
                                                        <div className="mt-3 p-3 bg-light rounded border">
                                                            <h6 className="mb-2">🤖 AI Model Image Generator</h6>
                                                            <p className="small text-muted mb-2">
                                                                Generate a professional model-wearing image from your flat-lay
                                                                photo
                                                            </p>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() => handleGenerateAIImage(variantIndex)}
                                                                disabled={variant.isGeneratingAI}
                                                            >
                                                                {variant.isGeneratingAI ? (
                                                                    <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                                                        Generating AI Image...
                                                                    </>
                                                                ) : (
                                                                    "✨ Generate AI Model Image"
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* NEW: AI Image Preview & Approval */}
                                                    {variant.aiGeneratedPreview && !variant.useAIImage && (
                                                        <div className="mt-3 p-3 bg-success bg-opacity-10 rounded border border-success">
                                                            <h6 className="mb-2 text-success">
                                                                ✓ AI Model Image Generated
                                                            </h6>
                                                            <p className="small mb-2">
                                                                Preview the AI-generated image and approve it to use as your
                                                                product image
                                                            </p>
                                                            <img
                                                                src={variant.aiGeneratedPreview}
                                                                alt="AI Generated"
                                                                className="img-fluid rounded mb-3"
                                                                style={{ maxHeight: "300px" }}
                                                            />
                                                            <div className="d-flex gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={() => handleApproveAIImage(variantIndex)}
                                                                >
                                                                    ✓ Approve & Use This Image
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => handleGenerateAIImage(variantIndex)}
                                                                >
                                                                    🔄 Regenerate
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleRejectAIImage(variantIndex)}
                                                                >
                                                                    × Discard
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* NEW: AI Image Approved Status */}
                                                    {variant.useAIImage && (
                                                        <div className="mt-3 p-3 bg-primary bg-opacity-10 rounded border border-primary">
                                                            <h6 className="mb-2 text-primary">
                                                                ✓ Using AI-Generated Model Image
                                                            </h6>
                                                            <img
                                                                src={variant.aiGeneratedPreview}
                                                                alt="AI Generated - Approved"
                                                                className="img-fluid rounded mb-2"
                                                                style={{ maxHeight: "200px" }}
                                                            />
                                                            <p className="small mb-2">
                                                                This AI-generated image will be saved as your product image
                                                            </p>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleRejectAIImage(variantIndex)}
                                                            >
                                                                × Remove AI Image
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Video Generation Section */}
                                                    {variant.useAIImage && (
                                                        <div className="mt-3 p-3 rounded border"
                                                             style={{ background: "rgba(0,46,106,0.05)", borderColor: "#002e6a" }}>
                                                            <h6 className="mb-2" style={{ color: "#002e6a" }}>
                                                                🎬 AI Product Video Generator
                                                            </h6>
                                                            <p className="small text-muted mb-2">
                                                                Generate a cinematic 10-second product video from your AI model image
                                                            </p>

                                                            {!variant.aiGeneratedVideo ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm"
                                                                    style={{ background: "#002e6a", color: "white" }}
                                                                    onClick={() => handleGenerateVideo(variantIndex)}
                                                                    disabled={variant.isGeneratingVideo}
                                                                >
                                                                    {variant.isGeneratingVideo ? (
                                                                        <>
                        <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                                                                            Generating Video... (usually 5–8 min, keep this page open)
                                                                        </>
                                                                    ) : (
                                                                        "🎬 Generate Product Video"
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <div>
                                                                    <video
                                                                        src={variant.aiGeneratedVideo}
                                                                        controls
                                                                        playsInline
                                                                        className="w-100 rounded mb-2"
                                                                        style={{maxHeight: "300px"}}
                                                                    />
                                                                    <div className="d-flex gap-2 flex-wrap">
                                                                        <a
                                                                        href={variant.aiGeneratedVideo}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-success btn-sm"
                                                                        onClick={(e) => {
                                                                        // Desktop download
                                                                        if (!/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
                                                                            e.preventDefault();
                                                                            const link = document.createElement("a");
                                                                            link.href = variant.aiGeneratedVideo;
                                                                            link.download = "product-video.mp4";
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                            document.body.removeChild(link);
                                                                        }
                                                                        // iPhone — opens in new tab, user can long press video to save
                                                                    }}
                                                                        >
                                                                        ⬇ Download Video
                                                                    </a>
                                                                    <p className="small text-muted mt-1 w-100">
                                                                        📱 On iPhone: tap "Download Video" then long
                                                                        press the video to save
                                                                    </p>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => handleGenerateVideo(variantIndex)}
                                                                        disabled={variant.isGeneratingVideo}
                                                                    >
                                                                        🔄 Regenerate
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() =>
                                                                            setVariants(prevVariants =>
                                                                                prevVariants.map((v, idx) =>
                                                                                    idx === variantIndex
                                                                                        ? {...v, aiGeneratedVideo: null}
                                                                                        : v
                                                                                )
                                                                            )
                                                                        }
                                                                    >
                                                                        × Remove Video
                                                                    </button>
                                                                </div>
                                                                </div>
                                                                )}
                                                        </div>
                                                        )}

                                                    {formErrors[`variant_${variantIndex}_images`] && (
                                                        <div className="text-danger mt-2">
                                                            {formErrors[`variant_${variantIndex}_images`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        ))}


                                    <button
                                        type="button"
                                        className="btn btn-outline-primary w-100 mb-3"
                                        onClick={handleAddVariant}
                                    >
                                        + Add Another Color Variant
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="row">
                                <div className="col-12 text-center">
                                    <button type="submit" className="btn btn-primary btn-lg px-5">
                                        Create {variants.length} Product{variants.length > 1 ? "s" : ""}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ProductAddPage;